import React from 'react';
import Modal from '@tencent/dui/lib/components/Modal';
import { tencentDocOpenUrlReload } from '@tencent/tencent_doc_open_url';
import MobileRemoteControlInfo, { QrCodeType } from './info';
import i18n from '@i18next/i18n';
import Dropdown from '@com-pc/dropdown';
import Snackbar from '@tencent/dui/lib/components/Snackbar';
import {
    PlayActionEventsKeys,
    PlayActionFeature,
} from '@src/framework/workbench/ui/titlebar/features/play-action-feature';
import { InjectorService } from '@tencent/workbench';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { IMobileRemoteControlDropdown, IMobileRemoteControlDropdownService } from '@src/slide/application/service/mobile-remote-control-dropdown/core/interface';
import { MOBILE_JOIN } from '@src/slide/application/service/remote-control-manager/core/constant';
import { IStatePointEnum } from '@src/slide/application/service/slide-render-state/slide-render-state-manager.interface';

export default class MobileRemoteControlDropdown implements IMobileRemoteControlDropdown {
    private dropdown: ReturnType<typeof Dropdown.create> | null;
    // 手机成功扫描二维码，pc 可进入播放台
    private scanCompleted = false;
    private services: IMobileRemoteControlDropdownService;

    public setServiceDelegates(services: IMobileRemoteControlDropdownService): void {
        this.services = services;
    }

    public open = async () => {
        const remoteControlManager = await this.services.remoteControlManager.promise();
        remoteControlManager.initRoomInfoAndEnter().catch(() => {})
            .finally(() => {
                if (!this.dropdown) {
                    this.dropdown = Dropdown.create({
                        belongID: 'mobile-control-btn',
                        scrollable: false,
                        center: 'center',
                        hasShadow: false,
                        isTransparent: true,
                        zIndex: 'var(--z-index-modal-negative)',
                    });
                }
                window.log.info('mobileRemoteControlLog-dropdown: open');
                this.updateDropContent();
                this.dropdown.show();
            });
        // 监听手机端加入事件
        remoteControlManager.event.on(MOBILE_JOIN, this.handleMobileJoin);
    };

    private handleClose = async () => {
        // 关闭时重置扫描状态
        this.scanCompleted = false;
        if (this.dropdown) {
            this.dropdown.close();
        }
        this.dropdown = null;
        const remoteControlManager = await this.services.remoteControlManager.promise();
        remoteControlManager.event.off(MOBILE_JOIN, this.handleMobileJoin);
    };

    private updateDropContent = () => {
        if (!this.dropdown) {
            return;
        }
        this.dropdown.updateDropContent(this.getDropContent());
    };

    private getDropContent = () => {
        const { loginType, wxOpenid } = this.services.extensionService.getDocInfo()?.userInfo || {};
        const LoginType = {
            QQ: 1,
            WX: 2,
        };
        // 获取用户登录类型，根据这个决定是生成 qq 小程序码还是微信小程序码
        const qrCodeType = (loginType === LoginType.QQ && !wxOpenid) ? QrCodeType.qq : QrCodeType.wx;
        window.log.info(`mobileRemoteControlLog-dropdown: url=${this.services.remoteControlManager.dangerousInstance?.remoteControlUrl}, qrCodeType=${qrCodeType}`);
        return (
            <MobileRemoteControlInfo
                scanCompleted={this.scanCompleted}
                // 此时 remoteControlManager 已经加载过了，所以直接用 dangerousInstance 没问题
                url={this.services.remoteControlManager.dangerousInstance?.remoteControlUrl || ''}
                onCreateRoom={this.handleCreateRoom}
                onCloseRoom={this.handleCloseRoom}
                onClose={this.handleClose}
                onPlay={this.handlePlay}
                qrCodeType={qrCodeType}
            />
        );
    };

    private handleCreateRoom = async () => {
        // 由于目前后台限制一个用户在一个文档中同时只能创建一个房间，所以需要在创建遥控器房间时先关闭远程演示的房间
        await this.closeMeetingRoom();
        const remoteControlManager = await this.services.remoteControlManager.promise();
        remoteControlManager.createShow((result) => {
            if (result.error) {
                Modal.confirm({
                    content: i18n.t('信息未成功拉取，请刷新后重试'),
                    onOk: () => {
                        tencentDocOpenUrlReload();
                    },
                    okText: i18n.t('立即刷新'),
                    cancelText: null,
                });
                return;
            }
            remoteControlManager.initRoomInfoAndEnter();
            window.log.info(`mobileRemoteControlLog-dropdown: createRoom roomid=${remoteControlManager.roomInfo().roomid}`);
            this.updateDropContent();
        });
    };

    private async closeMeetingRoom() {
        InjectorService.get(PlayActionFeature).event.emit(PlayActionEventsKeys.PLAY_ACTION_ACTIVE_CHANGE, false);
        const liveManager = await this.services.liveManager.promise();
        await liveManager.closeAndStopLive();
    }

    private handleCloseRoom = async () => {
        const remoteControlManager = await this.services.remoteControlManager.promise();
        remoteControlManager.closeAndStopLive();
        window.log.info('mobileRemoteControlLog-dropdown: manual closeRoom');
        this.handleClose();
    };

    // pc 进入演示
    private handlePlay = () => {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.MOBILE_CONTROLLER,
            action: TDWActionType.MOBILE_CONTROLLER_PAIR_SUCCESS,
        });
        this.handleClose();
        // 开始 pc 端的演示
        if (this.services.slideRenderStateManager.getStatePoints()[IStatePointEnum.allSlideConvert].isCompleted) {
            window.log.info('mobileRemoteControlLog-dropdown: play');
            setTimeout(async () => {
                const remoteControlManager = await this.services.remoteControlManager.promise();
                remoteControlManager.startPCPlay();
            }, 1);
        } else {
            Snackbar.show({
                message: i18n.t('加载幻灯片中，请稍后再试'),
                duration: 3000,
            });
        }
    };

    // 移动端进入房间后，pc 可以进入演示
    private handleMobileJoin = () => {
        this.scanCompleted = true;
        window.log.info('mobileRemoteControlLog-dropdown: mobileJoin');
        this.updateDropContent();
    };
}
